'use client'

import { useEffect, useState } from 'react'

import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import { useFetch, ResponseCode } from '@marketplace-web/core-api/api-client-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { ConversationActionProps } from '@marketplace-web/messaging/conversation-action-data'
import { ConversationActionButton } from '@marketplace-web/messaging/conversation-action-feature'
import {
  clickEvent,
  updateDeliveryOptionChangeRequest,
  getShipmentDetails,
  ShipmentDeliveryType,
} from '@marketplace-web/conversation-shipping/conversation-shipping-data'

import GenericErrorModal from '../GenericErrorModal'
import AcceptChangeDeliveryOptionRequestModal from './AcceptChangeDeliveryOptionRequestModal'
import { DeliveryOptionChangeRequestStatus } from '../../constants'

const AcceptChangeDeliveryOptionRequest = ({
  title,
  primary,
  size,
  theme,
  inverse,
}: ConversationActionProps) => {
  const { track } = useTracking()

  const { conversation } = useConversationContext()

  const transactionId = conversation?.transaction?.id
  const shipmentId = conversation?.transaction?.shipment?.id
  const shippingOrderId = conversation?.transaction?.shippingOrderId

  const { fetch: fetchShipmentDetails, data: shipmentDetails } = useFetch(getShipmentDetails)

  const [isChangeDeliveryOptionRequestModalOpen, setIsChangeDeliveryOptionRequestModalOpen] =
    useState(false)
  const [isDataLoading, setIsDataLoading] = useState(false)
  const [dataLoadingError, setDataLoadingError] = useState(false)

  useEffect(() => {
    if (!shipmentId) return
    if (shipmentDetails) return

    fetchShipmentDetails(shipmentId)
  }, [shipmentId, shipmentDetails, fetchShipmentDetails])

  const handleSubmit = async () => {
    setIsDataLoading(true)

    if (!shipmentId) return

    const response = await updateDeliveryOptionChangeRequest({
      shipmentId,
      status: DeliveryOptionChangeRequestStatus.Accepted,
    })

    if ('errors' in response) {
      setIsDataLoading(false)
      setDataLoadingError(true)

      return
    }

    if (response.code === ResponseCode.Ok) {
      setIsDataLoading(false)
    }
  }

  const trackButtonClick = () => {
    if (!transactionId) return

    const targetDetails = {
      transaction_id: transactionId,
    }

    track(
      clickEvent({
        target: 'accept_carrier_change_request',
        screen: 'message_reply',
        targetDetails: JSON.stringify(targetDetails),
      }),
    )
  }

  const handleClick = () => {
    trackButtonClick()

    if (shipmentDetails?.delivery_type === ShipmentDeliveryType.Home) {
      handleSubmit()

      return
    }

    setIsChangeDeliveryOptionRequestModalOpen(true)
  }

  const handlChangeDeliveryOptionRequestModalClose = () =>
    setIsChangeDeliveryOptionRequestModalOpen(false)

  const handleErrorModalClose = () => setDataLoadingError(false)

  return (
    <>
      <ConversationActionButton
        text={title}
        primary={primary}
        size={size}
        theme={theme}
        inverse={inverse}
        onClick={handleClick}
        testId="select-new-pickup-point"
        isLoading={isDataLoading}
      />
      <AcceptChangeDeliveryOptionRequestModal
        shipmentId={shipmentId}
        shippingOrderId={shippingOrderId}
        isOpen={isChangeDeliveryOptionRequestModalOpen}
        onClose={handlChangeDeliveryOptionRequestModalClose}
      />
      <GenericErrorModal isShown={dataLoadingError} onClose={handleErrorModalClose} />
    </>
  )
}

export default AcceptChangeDeliveryOptionRequest
